import { useState, useEffect } from "react";
import { API , authFetch, useEditMode, useAuth } from "@/App";
import { toast } from "sonner";
import { Receipt, Plus, Search, Check, X, MoreHorizontal, Camera, Image, Pause, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ApprovalActionDialog, canApprove } from "@/components/ApprovalActionDialog";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { ExpenseDetailDialog } from "@/components/ExpenseDetailDialog";

const ExpensesPage = () => {
  const canEdit = useEditMode();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const canApproveExpenses = canApprove(user);
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    period: new Date().toISOString().slice(0, 7),
    items: [{ date: "", category: "TRAVEL", description: "", amount: 0 }],
    travel_kms: 0,
    travel_amount: 0,
  });
  const [expPhotoFiles, setExpPhotoFiles] = useState([]);
  const [detailOpen, setDetailOpen] = useState(null); // expense object

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async ({ silent = false } = {}) => {
    try {
      let url = `${API}/expenses?`;
      if (statusFilter && statusFilter !== "all") url += `status=${statusFilter}&`;

      const [expensesRes, employeesRes] = await Promise.all([
        authFetch(url, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
      ]);

      const expensesData = await expensesRes.json();
      const employeesData = await employeesRes.json();

      if (expensesData.success) setExpenses(expensesData.data);
      if (employeesData.success) setEmployees(employeesData.data);
    } catch (error) {
      if (!silent) toast.error("Failed to load expenses");
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const { lastUpdatedAt, refreshing, manualRefresh } = useAutoRefresh(fetchData, {
    user,
    eventTypes: ["expense"],
    pollIntervalMs: 30000,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let photos = [];
      if (expPhotoFiles.length > 0) {
        const fd = new FormData();
        expPhotoFiles.forEach(f => fd.append("files", f));
        const uploadRes = await authFetch(`${API}/upload-multiple?category=expenses`, { method: "POST", body: fd });
        const uploadData = await uploadRes.json();
        if (uploadData.success) photos = uploadData.data.map(d => d.storage_path);
      }
      const items = formData.items.map((item) => ({
        ...item,
        amount: parseInt(item.amount) * 100,
      }));

      const response = await authFetch(`${API}/expenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          ...formData,
          items,
          travel_amount: parseInt(formData.travel_amount) * 100,
          photos,
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Expense submitted");
        setIsDialogOpen(false);
        resetForm();
        setExpPhotoFiles([]);
        fetchData();
      } else {
        toast.error(data.error || "Operation failed");
      }
    } catch (error) {
      toast.error("Failed to submit expense");
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      const response = await authFetch(`${API}/expenses/${expenseId}/approve`, {
        method: "POST",
        
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Expense approved");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to approve expense");
    }
  };

  const handleReject = async (expenseId) => {
    try {
      const response = await authFetch(`${API}/expenses/${expenseId}/reject`, {
        method: "POST",
        
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Expense rejected");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to reject expense");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      period: new Date().toISOString().slice(0, 7),
      items: [{ date: "", category: "TRAVEL", description: "", amount: 0 }],
      travel_kms: 0,
      travel_amount: 0,
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { date: "", category: "TRAVEL", description: "", amount: 0 }],
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const formatCurrency = (paise) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(paise / 100);
  };

  const getStatusBadge = (status) => {
    const styles = {
      SUBMITTED: "badge-info",
      APPROVED: "badge-success",
      REJECTED: "badge-danger",
      REVISION_REQUESTED: "badge-warning",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || "badge-default"}`}>
        {status}
      </span>
    );
  };

  // Region-filtered slice — falls back to employee.region_id
  const employeeById = employees.reduce((acc, e) => {
    if (e?.employee_id) acc[e.employee_id] = e;
    return acc;
  }, {});
  const regionExpenses = filterByRegion(expenses, (e) =>
    e.region_id || employeeById[e.employee_id]?.region_id
  );

  // Calculate totals (region-aware)
  const totalExpenses = regionExpenses.reduce((sum, e) => sum + e.total_amount, 0);
  const pendingCount = regionExpenses.filter((e) => e.status === "SUBMITTED").length;

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Expenses</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 flex items-center gap-2">
            Field expense management
            {lastUpdatedAt && (
              <span className="text-[10px] text-slate-400">
                · Last updated {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="expenses-region-switcher"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            className="h-9"
            data-testid="expenses-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        {/* Submit Expense button removed — expenses are filed from the mobile app */}
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {pendingCount > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="pending-expenses-alert">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">{pendingCount} expense report{pendingCount > 1 ? "s" : ""} awaiting approval</p>
              <p className="text-sm text-amber-600">Filter to Submitted and approve / reject field team expenses.</p>
            </div>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setStatusFilter("SUBMITTED")} data-testid="review-pending-expenses">Review Now</Button>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-slate-500">Total Expenses</p>
          <p className="text-2xl font-bold text-slate-900 font-mono mt-1">
            {formatCurrency(totalExpenses)}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-500">Pending Approvals</p>
          <p className="text-2xl font-bold text-amber-600 font-mono mt-1">{pendingCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-500">Total Reports</p>
          <p className="text-2xl font-bold text-slate-900 font-mono mt-1">{expenses.length}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Expenses Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="expenses-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Employee</th>
                <th>Period</th>
                <th>Items</th>
                <th>Travel (KMs)</th>
                <th className="text-right">Total Amount</th>
                <th>Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : regionExpenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="empty-state">
                      <Receipt className="empty-state-icon" />
                      <p className="empty-state-title">No expenses found</p>
                      <p className="empty-state-description">
                        Submit expense reports for reimbursement.
                      </p>
                      <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Submit Expense
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                regionExpenses.map((expense) => {
                  const employee = employees.find((e) => e.employee_id === expense.employee_id);
                  const photoCount = (expense.photos || []).length;
                  return (
                    <tr key={expense.expense_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="font-mono text-xs">
                        <button
                          type="button"
                          onClick={() => setDetailOpen(expense)}
                          className="text-[#8B1A1A] hover:underline"
                          data-testid={`open-expense-${expense.expense_id}`}
                        >
                          {expense.expense_id}
                        </button>
                      </td>
                      <td className="font-medium">{employee?.full_name || "-"}</td>
                      <td>{expense.period}</td>
                      <td className="flex items-center gap-1.5">
                        {expense.items?.length || 0}
                        {photoCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-violet-50 text-violet-700 border border-violet-200"
                            title={`${photoCount} receipt photo${photoCount > 1 ? "s" : ""}`}
                            data-testid={`expense-photo-badge-${expense.expense_id}`}
                          >
                            <Image className="w-2.5 h-2.5" /> {photoCount}
                          </span>
                        )}
                      </td>
                      <td className="font-mono">{expense.travel_kms || 0}</td>
                      <td className="numeric font-bold">
                        {formatCurrency(expense.total_amount)}
                      </td>
                      <td>{getStatusBadge(expense.status)}</td>
                      <td>
                        {(expense.status === "SUBMITTED" || expense.status === "ON_HOLD") && canApproveExpenses ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-100"
                              onClick={() => setDetailOpen(expense)}
                              data-testid={`view-expense-${expense.expense_id}`}
                              title="View details"
                            >
                              <Receipt className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                              onClick={() => setApprovalDialog({ entityId: expense.expense_id, entityLabel: `${employee?.full_name || ""} · ${expense.period} · ${formatCurrency(expense.total_amount)}`, action: "APPROVE" })}
                              data-testid={`approve-expense-${expense.expense_id}`}
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            {expense.status === "SUBMITTED" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50"
                                onClick={() => setApprovalDialog({ entityId: expense.expense_id, entityLabel: `${employee?.full_name || ""} · ${expense.period} · ${formatCurrency(expense.total_amount)}`, action: "HOLD" })}
                                data-testid={`hold-expense-${expense.expense_id}`}
                                title="Hold"
                              >
                                <Pause className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => setApprovalDialog({ entityId: expense.expense_id, entityLabel: `${employee?.full_name || ""} · ${expense.period} · ${formatCurrency(expense.total_amount)}`, action: "REJECT" })}
                              data-testid={`reject-expense-${expense.expense_id}`}
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setDetailOpen(expense)}
                                data-testid={`view-expense-details-${expense.expense_id}`}
                              >
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Submit Expense Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>Submit Expense Report</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Employee</label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Period</label>
                <Input
                  type="month"
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Expense Items</label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
              {formData.items.map((item, index) => (
                <div key={index} className="grid grid-cols-4 gap-2">
                  <Input
                    type="date"
                    value={item.date}
                    onChange={(e) => updateItem(index, "date", e.target.value)}
                    placeholder="Date"
                  />
                  <Select
                    value={item.category}
                    onValueChange={(value) => updateItem(index, "category", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRAVEL">Travel</SelectItem>
                      <SelectItem value="MEALS">Meals</SelectItem>
                      <SelectItem value="ENTERTAINMENT">Entertainment</SelectItem>
                      <SelectItem value="PRINTING">Printing</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                    placeholder="Description"
                  />
                  <Input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateItem(index, "amount", e.target.value)}
                    placeholder="₹"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Travel KMs</label>
                <Input
                  type="number"
                  value={formData.travel_kms}
                  onChange={(e) => setFormData({ ...formData, travel_kms: parseFloat(e.target.value) })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Travel Amount (₹)</label>
                <Input
                  type="number"
                  value={formData.travel_amount}
                  onChange={(e) => setFormData({ ...formData, travel_amount: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <label className="text-sm font-medium text-slate-700">Receipt / Bill Photos (optional)</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-2.5 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#8B1A1A] bg-slate-50 transition-colors" data-testid="expense-photo-upload">
                <Camera className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">{expPhotoFiles.length > 0 ? `${expPhotoFiles.length} file(s) selected` : "Click to upload photos"}</span>
                <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => setExpPhotoFiles(Array.from(e.target.files || []))} />
              </label>
              {expPhotoFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {expPhotoFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                      <Image className="w-3 h-3" /> {f.name}
                      <button type="button" onClick={() => setExpPhotoFiles(expPhotoFiles.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Submit Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unified Approval/Reject/Hold Dialog */}
      {approvalDialog && (
        <ApprovalActionDialog
          open={!!approvalDialog}
          onClose={() => setApprovalDialog(null)}
          entity="expense"
          action={approvalDialog.action}
          entityId={approvalDialog.entityId}
          entityLabel={approvalDialog.entityLabel}
          endpointBase="/expenses"
          onSuccess={() => fetchData()}
        />
      )}

      {detailOpen && (
        <ExpenseDetailDialog
          open={!!detailOpen}
          onClose={() => setDetailOpen(null)}
          expense={detailOpen}
          employee={employees.find((e) => e.employee_id === detailOpen.employee_id)}
        />
      )}
    </div>
  );
};

export default ExpensesPage;
